/* ============================================================================
   UI / form — fields and their controls.
   Input, Textarea, PasswordInput, Field (label around a control), Select, Checkbox, Range (slider), Switch (on / off).
   ============================================================================ */
import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { cx } from "./util.js";

/* A one-line text box of any type (text, email, number, date, time, tel); wrap it in Field to give it a label. */
export const Input = React.forwardRef(function Input({ className, ...props }, ref) {
  return <input ref={ref} className={cx("ds-input", className)} {...props} />;
});

/* A multi-line text box; looks like Input and takes the same props (rows, maxLength, value, onChange). */
export const Textarea = React.forwardRef(function Textarea({ className, ...props }, ref) {
  return <textarea ref={ref} className={cx("ds-input", className)} {...props} />;
});

/* A password box with a control at its right end that shows or hides what is typed (ERP-1990). */
export const PasswordInput = React.forwardRef(function PasswordInput({ className, ...props }, ref) {
  const [shown, setShown] = useState(false);
  return (
    <span className="ds-password">
      <Input ref={ref} type={shown ? "text" : "password"} className={className} {...props} />
      <button type="button" className="ds-password__toggle" aria-label={shown ? "Hide password" : "Show password"} aria-pressed={shown} onClick={() => setShown((v) => !v)}>
        {shown ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
      </button>
    </span>
  );
});

/* A label around its control, so clicking the label reaches the field and a screen reader reads them together.
   `as="div"` is for a heading over several controls (a field with its button beside it), where the label would claim
   clicks that belong to a control inside it. */
export function Field({ as: Tag = "label", label, hint, error, className, children }) {
  return (
    <Tag className={cx("ds-field", className)}>
      {label && <span className="ds-label">{label}</span>}
      {children}
      {error ? <span className="ds-field__error">{error}</span>
        : hint ? <span className="ds-field__hint">{hint}</span> : null}
    </Tag>
  );
}

/* A dropdown. options: ["A", "B"] or [{ value, label }]; `placeholder` adds the empty first choice. */
export function Select({ options = [], placeholder, ...props }) {
  return (
    <select {...props}>
      {placeholder != null && <option value="">{placeholder}</option>}
      {options.map((o) => {
        const { value, label } = typeof o === "object" ? o : { value: o, label: o };
        return <option key={value} value={value}>{label}</option>;
      })}
    </select>
  );
}

/* A tick box with its words: the whole line is the target, and the label and the box are read together. */
export const Checkbox = React.forwardRef(function Checkbox({ label, className, ...props }, ref) {
  return (
    <label className={cx("ds-check", className)}>
      <input ref={ref} type="checkbox" {...props} />
      {label != null && <span>{label}</span>}
    </label>
  );
});

/* A slider for a number between min and max. onChange(number) gets the new value, not the event; `label` is its
   name for screen readers. */
export const Range = React.forwardRef(function Range({ label, onChange, min = 0, max = 100, className, ...props }, ref) {
  return <input ref={ref} type="range" min={min} max={max} aria-label={label} className={cx("ds-range", className)} onChange={(e) => onChange?.(Number(e.target.value))} {...props} />;
});

/* An on / off switch for something that takes effect at once (a person's login, a module). `label` is for screen readers. */
export function Switch({ on, onChange, label, disabled, tone, className, ...props }) {
  return (
    <button type="button" role="switch" aria-checked={Boolean(on)} aria-label={label} disabled={disabled}
      className={cx("ds-switch", tone && `ds-switch--${tone}`, className)} onClick={() => onChange?.(!on)} {...props}>
      <span />
    </button>
  );
}
